import React, { JSX } from "react";
import { Link } from "react-router-dom";
import { ProjectPerspective } from "@app/projects/enumerators";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { querystringify } from "@app/shared/utility/querystringify";
import Features from "@app/feature-flags/features";

const UNASSIGNED_REGION_TOOLTIP = "Only populated for counties assigned to a region";

/**
 * Builds the region name cell for the view-by-all and similar tables.
 * Returns a router Link when the row has both a region name and ID, otherwise "---".
 */
export function buildRegionNameCell(
  regionName: string | undefined,
  regionSetId: number | undefined,
  matchUrl: string
): JSX.Element | string {
  if (!regionName || !regionSetId) return "---";
  return (
    <span>
      <Link
        to={{
          pathname: `${matchUrl}/region/${regionSetId}`,
          search: querystringify({ regionName, viewBy: ProjectPerspective.ByCounty }),
        }}
      >
        {regionName}
      </Link>
    </span>
  );
}

/**
 * Builds the In-Region servicing count cell.
 * Returns the count string when populated, or a "---" with an explanatory tooltip
 * when the county is not assigned to a region.
 */
export function buildInRegionCountCell(
  servicingRegionalCount: string | undefined
): JSX.Element | string {
  if (servicingRegionalCount && servicingRegionalCount !== "---") {
    return servicingRegionalCount;
  }
  return (
    <TooltipWrapper text={UNASSIGNED_REGION_TOOLTIP}>---</TooltipWrapper>
  );
}

/**
 * Returns true when both conditions for regional analysis display are met:
 * the project uses a regional template and the feature flag is enabled.
 */
export function isRegionalAnalysisEnabled(
  hasRegions: boolean | undefined,
  enabledFeatureFlags: ReadonlySet<string>
): boolean {
  return !!hasRegions && enabledFeatureFlags.has(Features.REGIONAL_SERVICING_ANALYSIS);
}

export { UNASSIGNED_REGION_TOOLTIP };
